import { Injectable } from 'angular-ts-decorators';

import Dexie from 'dexie';

/**
 * The swDexie provider encapsulates the Dexie light IndexDb wrapper.
 *
 * Looks we want to use it mostly via {@link coreModule.localDraft localDraft}
 * service
 */
@Injectable('swDexie')
export class SwDexie {
  private db: Dexie;
  private _databaseName: string;

  constructor(
    private $log: ng.ILogService,
    private $q: ng.IQService,
    private Dexie,
  ) {
    'ngInject';

    this._databaseName = null;
  }

  set databaseName(value) {
    this._databaseName = value;
  }

  /**
   *
   * This method initializes swDexie. The appId should be set before calling
   * this method.
   * @param {object} collections the collections to be defined
   * @param {number} version the schema version
   *
   * @returns {object} $q promise that resolves empty if everything goes fine,
   * and rejects otherwise
   */
  // NOTE! each call to swDexie will override existing db object; ie we cannot use
  // on different collections
  init(
    collections: { [key: string]: string | null },
    version: number,
  ): ng.IPromise<Dexie> {
    if (!this._databaseName) {
      // database name is required to boot up
      this.$log.error('swDexie: database name is a required parameter!');
      return this.$q.reject();
    }

    if (!version || !angular.isNumber(version)) {
      this.$log.error('swDexie: version is a required param!');
      return this.$q.reject();
    }

    if (!collections) {
      this.$log.error('swDexie: collection is a required param!');
      return this.$q.reject();
    }

    this.db = new this.Dexie(this._databaseName);

    this.db.version(version).stores(collections);

    return this.$q((resolve, reject) => {
      this.db
        .open()
        .then((db) => {
          this.$log.debug('swDexie Opened Successfully');
          this.$log.debug(`Database version: ${db.verno}`);
          this.$log.debug(`Database name: ${db.name}`);

          resolve(db);
        })
        .catch((error) => {
          reject(error);
          this.$log.error('swDexie: caught an error', error);
        });
    });
  }

  /**
   *
   * This method can be used to get a key
   * @param {string} table the name of the table in the database.
   * @param {string} key the key to search by
   * @param {string} value the value to search for.
   *
   * @returns {object} $q promise that resolves to array with data
   */
  getByKey(table: string, key: string, value: string): ng.IPromise<object> {
    return this.$q((resolve, reject) => {
      this.db[table]
        .where(key)
        .equals(value)
        .toArray()
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          this.$log.debug(`${table} getByKey error - ${error}`);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to modify a key
   * @param {string} table the name of the table in the database.
   * @param {string} key the key to search by
   * @param {string} value the value to search for.
   * @param {string} objToModify the object to modify.
   *
   * @returns {object} $q promise
   */
  modifyOne(
    table: string,
    key: string,
    value: string,
    objToModify: string,
  ): ng.IPromise<object> {
    return this.$q((resolve, reject) => {
      this.db[table]
        .where(key)
        .equals(value)
        .modify(objToModify)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          this.$log.debug(`${table} modifyOne error - ${error}`);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to get the number of items in a collection
   * @param {string} table the name of the table in the database.
   *
   * @returns {object} $q promise
   */
  count(table: string): ng.IPromise<number> {
    return this.$q((resolve, reject) => {
      this.db[table]
        .count()
        .then((count) => resolve(count))
        .catch((error) => {
          reject(null);
          this.$log.debug(`${table} count error - ${error}`);
        });
    });
  }

  /**
   *
   * This method can be used to get the list of items in a collection
   * @param {string} collection the name of the table in the database.
   *
   * @returns {object} $q promise
   */
  getCollection(collection: string): ng.IPromise<object[]> {
    return this.$q((resolve) => {
      this.db[collection]
        .toArray()
        .then((collection) => resolve(collection))
        .catch((error) => {
          resolve([]);
          this.$log.debug(`Get Collection ${collection} Error - `, error);
        });
    });
  }

  /**
   *
   * This method can be used to get a single item by key
   * @param {string} table the name of the table in the database.
   * @param {string} key the key of the item
   *
   * @returns {object} $q promise
   */
  findOne(table: string, key: string): ng.IPromise<object> {
    return this.$q((resolve, reject) => {
      key = key.toString();

      this.db[table]
        .get(key)
        .then((result) => {
          resolve(result && result.length ? result.value[0] : null);
        })
        .catch((error) => {
          this.$log.debug('swDexie: findOne Error - ', error);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to find an item with a filter
   * @param {string} table the name of the table in the database.
   * @param {string} key the filter key
   * @param {string} value the filter value
   *
   * @returns {object} $q promise
   */
  findOneWhere(table: string, key: string, value: string): ng.IPromise<object> {
    key = key.toString();

    return this.$q((resolve, reject) => {
      this.db[table]
        .where(key)
        .equals(value)
        .toArray()
        .then((result) => {
          if (result.length > 0) {
            resolve(result[0]);
          } else {
            resolve(null);
          }
        })
        .catch((error) => {
          this.$log.debug('swDexie: findOne Error - ', error);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to find several items matchig a query
   * @param {string} table the name of the table in the database.
   * @param {string} key the filter key
   * @param {string} value the filter value
   *
   * @returns {object} $q promise
   */
  findMany(table: string, key: string, value: string): ng.IPromise<object[]> {
    return this.$q((resolve, reject) => {
      key = key.toString();

      this.db[table]
        .where(key)
        .equals(value)
        .toArray()
        .then((result) => {
          resolve(result ? result : []);
        })
        .catch((error) => {
          this.$log.debug('swDexie: findMany Error - ', error);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to add a new key or update an existing a key in a collection
   * @param {string} collection the name of the collection in the database.
   * @param {object} value the item to add
   *
   * @returns {ng.IPromise<boolean>} $q promise
   */
  put(collection: string, value: object): ng.IPromise<boolean> {
    return this.$q((resolve, reject) => {
      this.db[collection]
        .put(angular.fromJson(angular.toJson(value)))
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to check if an item exists by its key
   * @param {string} collection the name of the collection in the database.
   * @param {string} key the key of the item
   *
   * @returns {object} $q promise
   */
  exists(collection: string, key: string): ng.IPromise<boolean> {
    return this.$q((resolve) => {
      key = key.toString();
      this.db[collection]
        .get(key)
        .then((result) => {
          resolve(!!result);
        })
        .catch((error) => {
          this.$log.debug('swDexie: Exists Error - ', error);
        });
    });
  }

  /**
   *
   * This method can be used to remove items by a key and value
   * @param {string} collection the name of the collection in the database.
   * @param {string} key the key to filter by
   * @param {string} value the value to filter by
   *
   * @returns {object} $q promise
   */
  remove(collection: string, key: string, value: string): ng.IPromise<string> {
    return this.$q((resolve) => {
      key = key.toString();
      this.db[collection]
        .where(key)
        .equals(value)
        .delete()
        .then((result) => resolve(result))
        .catch((error) => {
          this.$log.debug('swDexie: Remove Error - ', error);
          resolve(error);
        });
    });
  }

  /**
   * @deprecated
   * This method can be used to remove a database
   *
   * Note! it will not work as expected & is never used so far
   * @returns {object} $q promise
   */
  deleteDB(): ng.IPromise<object> {
    return this.$q((resolve, reject) => {
      this.db
        .delete()
        .then(resolve)
        .catch((error) => {
          this.$log.debug('swDexie: Delete DB Error - ', error);
          reject(error);
        });
    });
  }

  /**
   *
   * This method will bulk upsert an array of keys. So if the key doesn't
   * exist it will be added. If the key exits, it will be updated.
   * @param {string} table the name of the table in the database.
   * @param {array} items the items to add
   *
   * @returns {ng.IPromise<boolean>} $q promise
   */
  bulkPut(table: string, items: object[]): ng.IPromise<boolean> {
    return this.$q((resolve, reject) => {
      this.db[table]
        .bulkPut(angular.fromJson(angular.toJson(items)))
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          this.$log.debug('swDexie: bulkPut Error - ', error);
          reject(error);
        });
    });
  }

  /**
   *
   * This method can be used to drop any table by name
   * @param {string} collection the name of the collection in the database.
   *
   * @returns {object} $q promise
   */
  dropCollection(collection: string): ng.IPromise<object> {
    return this.$q((resolve, reject) => {
      this.db[collection]
        .clear()
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * This method can be used to close database connection
   * @return {Void} nothing
   */
  close(): void {
    if (this.db) {
      this.db.close();
    }
  }
}
